import {Team, APIResponse, Difficulty, Booking, GamePlay} from "../models/models"
import {ApiBase} from "./api-base"

export default class Api extends ApiBase {
    static async ping(): Promise<APIResponse> {
        return await this.doApiCall("get", "/")
    }

    static async getVenue() {
        return await this.doApiCall("get", "/venue")
    }

    /*
        Time slots & registration
    */
    static async getNextTeamNumber(teams: number): Promise<APIResponse> {
        return await this.doApiCall("get", "/next-team-number?teams=" + teams)
    }

    static async getNextFreeSlot(playTime: number, teamCount: number, registerId: number, bookingId: string | undefined = undefined): Promise<number> {
        const response = await this.doApiCall("get", `/next-free-slot?playTime=${playTime}&registerId=${registerId}&teamCount=${teamCount}&bookingId=${bookingId}`)
        return response.date
    }

    static async getPrices(): Promise<any> {
        return await this.doApiCall("get", `/get-prices`)
    }

    static async getBooking(barcode: string): Promise<Booking | null> {
        const response = await this.doApiCall("get", `/bookings/${barcode}`)
        if (response.booking !== null) {
            return Booking.toClass(response.booking)
        }
        return null
    }

    static async updateBooking(bookingId: string, fields: any): Promise<any> {
        return await this.doApiCall("post", `/bookings/${bookingId}`, {fields})
    }

    static async lockTimeSlots(registerId: number, teamCount: number, playTime = 3600): Promise<APIResponse> {
        const data = {teamCount, playTime}
        return await this.doApiCall("post", `/update-locked-slots/${registerId}`, data)
    }

    static async getRfidStatus(rfid: string): Promise<any> {
        return await this.doApiCall("get", `/rfid/${rfid}`)
    }

    static async sendPaymentMail(paymentData: any): Promise<any> {
        return await this.doApiCall("post", "/payment-email", paymentData)
    }

    /*
        Teams
    */
    static async getActiveTeams(): Promise<Array<Team>> {
        const response = await this.doApiCall("get", "/active-teams")
        return response.teams
    }

    static async updateTeams(teams: Team[], fields: any): Promise<Team[] | undefined> {
        const response = await this.doApiCall("post", "/teams", {teams, fields})
        if (response.teams !== null) {
            return Team.toClassArray(response.teams)
        }
    }

    static async createTeams(teams: Array<Team>): Promise<any> {
        return await this.doApiCall("put", "/teams", {teams})
    }

    static async getTeamByRfid(rfid: string): Promise<Team | undefined> {
        const response = await this.doApiCall("get", `/team?rfid=${rfid}`)
        if (response.team !== null) {
            return Team.toClass(response.team)
        }
    }

    static async saveRfid(teamId: string, playerId: string|undefined, rfid: string): Promise<any> {
        return await this.doApiCall("post", `/save-rfid`, {teamId, playerId, rfid})
    }

    static async searchTeams(query: any): Promise<Array<Team> | undefined> {
        const response = await this.doApiCall("post", "/search-teams", query)
        if (response.teams !== null) {
            return Team.toClassArray(response.teams)
        }
    }

    static async updateTeam(teamId: string, data: any): Promise<Team> {
        const response = await this.doApiCall("post", `/team/${teamId}`, {data})
        return Team.toClass(response.team)
    }

    static async deleteTeam(id: string): Promise<APIResponse> {
        return await this.doApiCall("delete", "/team", {id})
    }

    static async checkEntry(teamId: string): Promise<any> {
        return await this.doApiCall("get", `/team/${teamId}/entry`)
    }

    /*
        Gameplay & rooms
    */
    static async getRoomStatus(roomId: string) {
        return await this.doApiCall("get", `/room/${roomId}`, {roomId})
    }

    static async startGame(teamId: string, gameId: string, difficulty: Difficulty, useBooster: boolean) {
        return await this.doApiCall("post", "/start-game", {teamId, gameId, difficulty, useBooster})
    }

    static async saveGameplay(teamId: string, gameplay: GamePlay) {
        const saveData = gameplay as any
        saveData.teamId = teamId
        return await this.doApiCall("post", "/save-gameplay", saveData)
    }

    static async getTeamScore(teamId: string) {
        return await this.doApiCall("get", `/team/${teamId}/score`)
    }

    static async getRanking(percentage: number) {
        return await this.doApiCall("get", "/ranking?percentage=" + percentage)
    }

    static async getLeaderboards() {
        return await this.doApiCall("get", "/leaderboards")
    }

    static async getAllTimeLeaderboards() {
        return await this.doApiCall("get", "/leaderboards-alltime")
    }

    static async getDailyLeaderboards() {
        return await this.doApiCall("get", "/leaderboards-daily")
    }

    static async saveForExport(paymentData: any) {
        return await this.doApiCall("post", "/save-for-export", paymentData)
    }

    // save team result to cloud with base64 screenshot
    static async saveTeamResult(teamId: string, screenshot: string) {
        return await this.doApiCall("post", `/team/${teamId}/result`, {screenshot})
    }
}
